import React, { useEffect, useState } from 'react';
import {  Grid, Box, Typography, Container, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import ProgressComponent from '../components/dashboard/ProgressComponent';
import NeighborhoodsComponent from '../components/dashboard/NeighborhoodsComponent';
import AssignedNeighborhoodsComponent from '../components/dashboard/AssignedNeighborhoodsComponent';
import AvailableCollaboratorsComponent from '../components/dashboard/AvailableCollaboratorsComponent';
import QuotasComponent from '../components/dashboard/QuotasComponent';
import SessionsTableComponent from '../components/dashboard/SessionsTableComponent';
import UserBadge from '../components/dashboard/UserBadge';
import MyVenuesComponent from "../components/dashboard/MyVenuesComponent";
import NeighborhoodQueryBox from '../components/dashboard/NeighborhoodQueryBox';


// Set Axios base URL from environment variable
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL;

const BookingDashboard = () => {
    const [employeeName, setEmployeeName] = useState('');
    const [formattedDate, setFormattedDate] = useState('');
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [locationDetails, setLocationDetails] = useState({ city: '', state: '', country: '', county: '' });
    const [mapCenter, setMapCenter] = useState({ lat: 37.7749, lng: -122.4194 }); // 🟡 Default SF (temporary)
    const [sport, setSport] = useState('');
    const [aiPins, setAiPins] = useState([]);
    const [isMapExpanded, setIsMapExpanded] = useState(false);

    const handleToggleMapExpand = () => {
        setIsMapExpanded(!isMapExpanded);
    };


    const handleCenterOnLocation = (location) => {
        if (location && location.lat && location.lng) {
            setMapCenter({
                lat: location.lat,
                lng: location.lng,
                zoom: 15, // Zoom in close to the specific venue
            });
        }
    };

    useEffect(() => {
        const auth = getAuth();
        console.log('Setting up auth state change listener');

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log('Auth state changed:', user);
            if (user) {
                try {
                    const firebaseUid = user.uid;
                    console.log('Fetching employee details for firebaseUid:', firebaseUid);

                    const response = await axios.get(`/api/employees/${firebaseUid}/neighborhoods`);
                    console.log('Employee data fetched:', response.data);

                    if (response.data) {
                        const { neighborhoods, city, state, country, sport: employeeSport } = response.data;
                        setNeighborhoods(neighborhoods || []);
                        setLocationDetails({ city, state, country });

                        // 🔹 Fetch county and exact coordinates
                        fetchCountyAndSetMapCenter(city, state);

                        setSport(employeeSport || 'Unknown Sport');
                    } else {
                        console.warn('No data found for employee neighborhoods');
                    }

                    // Fetch employee name
                    const employeeResponse = await axios.get(`/api/employee/${firebaseUid}`);
                    if (employeeResponse.data) {
                        setEmployeeName(employeeResponse.data.name || 'Employee');
                    }

                } catch (error) {
                    console.error('Error fetching employee data:', error);
                    setEmployeeName('Employee');
                }
            } else {
                console.warn('User is not authenticated');
                setEmployeeName('Guest');
            }
        });

        const today = new Date();
        setFormattedDate(today.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }));

        return () => {
            console.log('Cleaning up auth state listener');
            unsubscribe();
        };
    }, []);

    // 🔹 Function to fetch county and set map center
    const fetchCountyAndSetMapCenter = async (city, state) => {
        try {
            const response = await axios.get(`/api/geocode/county`, { params: { city, state } });

            if (response.data) {
                const { county, lat, lng } = response.data;
                setLocationDetails(prev => ({ ...prev, county }));
                setMapCenter({ lat, lng });
                console.log(`Map centered to ${county}:`, { lat, lng });
            } else {
                console.error('County or coordinates not found');
            }
        } catch (error) {
            console.error('Error fetching county data:', error);
        }
    };

    const handleNeighborhoodClick = (neighborhood) => {
        console.log('Clicked neighborhood:', neighborhood);

        if (neighborhood.latitude && neighborhood.longitude) {
            setMapCenter({
                lat: neighborhood.latitude,
                lng: neighborhood.longitude,
                zoom: 14,
            });
        } else {
            console.warn('Neighborhood missing lat/lng:', neighborhood);
        }
    };

    return (
        // The outermost element is now a React Fragment (<>).
        // This prevents any styles from being applied that would cause an overflow.
        <>
            {/* The Modal for the full-screen map. It's always in the DOM, waiting to be opened. */}
            <Modal
                open={isMapExpanded}
                onClose={handleToggleMapExpand}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Box sx={{ width: '100vw', height: '100vh', p: { xs: 1, sm: 2, md: 4 }, boxSizing: 'border-box' }}>
                    <NeighborhoodsComponent
                        mapCenter={mapCenter}
                        setMapCenter={setMapCenter}
                        aiPins={aiPins}
                        isExpanded={isMapExpanded}
                        onToggleExpand={handleToggleMapExpand}
                    />
                </Box>
            </Modal>

            {/* This condition wraps the ENTIRE dashboard.
                It will only render when the map is NOT expanded. */}
        {!isMapExpanded && (
            <Box sx={{
                display: 'flex',          // It's a flex container.
                flexDirection: 'column',  // It stacks its children vertically.
                flexGrow: 1,              // It grows to fill the height of its parent from App.js.
            }}>
                <Container maxWidth="xl" disableGutters>
                    {/* Dashboard Header */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem', mb: 3 }}>
                        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>{employeeName} | {formattedDate}</Typography>
                        <UserBadge firebaseUid={getAuth().currentUser?.uid} />
                    </Box>

                    {/* Dashboard Grid */}
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <ProgressComponent firebaseUid={getAuth().currentUser?.uid} />
                        </Grid>
                        <Grid item xs={12}>
                            <NeighborhoodsComponent
                                mapCenter={mapCenter}
                                setMapCenter={setMapCenter}
                                aiPins={aiPins}
                                isExpanded={isMapExpanded}
                                onToggleExpand={handleToggleMapExpand}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: 3 }}>
                            <AssignedNeighborhoodsComponent
                                neighborhoods={neighborhoods}
                                userLocation={locationDetails}
                                handleNeighborhoodClick={handleNeighborhoodClick}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ mt: -1 }}>
                            <NeighborhoodQueryBox
                                onCenterLocation={handleCenterOnLocation}
                                mapCenter={mapCenter}
                                onLocationExtracted={(locations) => {
                                    setAiPins(locations);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MyVenuesComponent />
                        </Grid>
                        <Grid item xs={12}>
                            <AvailableCollaboratorsComponent />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <QuotasComponent quotaType="daily" address={locationDetails} sport={sport} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <QuotasComponent quotaType="weekly" address={locationDetails} sport={sport} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <QuotasComponent quotaType="monthly" address={locationDetails} sport={sport} />
                        </Grid>
                        <Grid item xs={12}>
                            <SessionsTableComponent />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        )}
        </>
    );
};

export default BookingDashboard;
